import { etagFromRow, get as baseGet, getVersioned as baseGetVersioned, post as basePost, postVersioned as basePostVersioned, putVersioned, seg, type RequestOptions } from '../client';
import type { Page } from '../types/common';
import type { FormResponse } from '../types/documents';
import type {
  CreatePortalRequest, CreatePortalThreadRequest, CreatePortalThreadResponse, PortalAllergy, PortalAppointment, PortalDocument, PortalDocumentContent, PortalForm,
  PortalMe, PortalMedication, PortalReplyResponse, PortalProxyGrant, PortalRefillRequest, PortalRequest, PortalResult, PortalThreadSummary,
} from '../types/portal';

type Versioned = { id: string; rowVersion?: number | string };
const idOf = (x: string | Versioned) => (typeof x === 'string' ? x : x.id);
const tagOf = (x: string | Versioned, etag?: string | null) => etag ?? (typeof x === 'string' ? null : etagFromRow(x.rowVersion));

/** Default text for a screen-only urgent escalation (R-011): never depends on what the patient typed. */
export const URGENT_DEFAULT_SUBJECT = 'Urgent symptom screening';
export const URGENT_DEFAULT_BODY = 'The patient answered yes to an urgent symptom question. Emergency guidance was shown.';

/** Fill a non-empty subject/body when any screen answer is yes (backend requires both for routine messages). */
export function withUrgentDefaults(body: CreatePortalThreadRequest): CreatePortalThreadRequest {
  const positive = Object.values(body.screen ?? {}).some(Boolean);
  if (!positive) return body;
  return { ...body, subject: body.subject?.trim() || URGENT_DEFAULT_SUBJECT, body: body.body?.trim() || URGENT_DEFAULT_BODY };
}

/** Decode a base64 string to bytes (browser + node). */
export function base64ToBytes(b64: string): Uint8Array {
  const clean = b64.replace(/\s+/g, '');
  const bin = atob(clean);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Document content → downloadable Blob, decoding the base64 branch (R-106). */
export function documentBlob(c: PortalDocumentContent): Blob {
  if (c.encoding === 'base64') {
    const bytes = base64ToBytes(c.contentBase64);
    return new Blob([bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer], { type: c.contentType || 'application/octet-stream' });
  }
  return new Blob([c.text], { type: c.contentType || 'text/plain' });
}

/** File name for a download: keep the server's extension; add .txt only for text without one. */
export function documentFilename(c: PortalDocumentContent): string {
  const base = c.filename.replace(/[^\w\s.-]+/g, '').trim() || 'document';
  if (/\.[A-Za-z0-9]{1,8}$/.test(base)) return base;
  if (c.encoding === 'base64') {
    const ext = c.contentType === 'application/pdf' ? '.pdf' : c.contentType.startsWith('image/') ? `.${c.contentType.slice(6).replace(/\W/g, '')}` : '';
    return base + ext;
  }
  return `${base}.txt`;
}

const withMeEtag = (data: PortalMe, etag: string | null): PortalMe => ({ ...data, etag: etag ?? etagFromRow(data.rowVersion) ?? null });

/** Patient/proxy APIs. Workforce tokens are rejected here and vice versa (IAM-002). */
let defaultSubject: string | undefined;
export const portalContextTabId = crypto.randomUUID();
export function portalForPatient(patientId?: string) {
  const options = (opts: RequestOptions = {}): RequestOptions => ({ ...opts, headers: { ...opts.headers, ...((patientId ?? defaultSubject) ? { 'X-Portal-Patient-Id': patientId ?? defaultSubject! } : {}) } });
  const get: typeof baseGet = (path, query, opts) => baseGet(path, query, options(opts));
  const post: typeof basePost = (path, body, opts) => basePost(path, body, options(opts));
  const postVersioned: typeof basePostVersioned = (path, body, opts) => basePostVersioned(path, body, options(opts));
  return {
  me: async () => {
    const r = await baseGetVersioned<PortalMe>('/portal/me');
    defaultSubject = r.data.activePatientId ?? undefined;
    return withMeEtag(r.data, r.etag);
  },
  /**
   * Switch whose record I am acting on (proxy subject). Server re-evaluates scope. If-Match is the portal context
   * version from GET /portal/me (read first when the caller does not pass it).
   */
  setContext: async (patientId: string, etag?: string | null) => {
    const ifMatch = etag ?? (await baseGetVersioned<PortalMe>('/portal/me')).etag ?? 'W/"0"';
    const r = await putVersioned<PortalMe>('/portal/context', { patientId }, { ifMatch });
    const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('etnas.portal.subject');
    if (channel) { channel.postMessage({ tabId: portalContextTabId }); channel.close(); }
    else try { localStorage.setItem('etnas.portal.subject', String(Date.now())); } catch { /* unavailable storage */ }
    return withMeEtag(r.data, r.etag);
  },
  appointments: (p: { cursor?: string; limit?: number } = {}) => get<Page<PortalAppointment>>('/portal/appointments', { cursor: p.cursor, limit: p.limit }),
  checkIn: (a: string | PortalAppointment, etag?: string | null) =>
    post<PortalAppointment>(`/portal/appointments/${seg(idOf(a))}:check-in`, {}, { idempotent: true, purpose: 'PATIENT_REQUEST', ifMatch: tagOf(a, etag) }),
  cancelAppointment: (a: string | PortalAppointment, reason: string, etag?: string | null) =>
    post<PortalAppointment>(`/portal/appointments/${seg(idOf(a))}:cancel`, { reason }, { idempotent: true, purpose: 'PATIENT_REQUEST', ifMatch: tagOf(a, etag) }),
  results: (p: { cursor?: string; limit?: number } = {}) => get<Page<PortalResult>>('/portal/results', { cursor: p.cursor, limit: p.limit }),
  medications: (p: { cursor?: string; limit?: number } = {}) => get<Page<PortalMedication>>('/portal/medications', { cursor: p.cursor, limit: p.limit }),
  allergies: (p: { cursor?: string; limit?: number } = {}) => get<Page<PortalAllergy>>('/portal/allergies', { cursor: p.cursor, limit: p.limit }),
  requestRefill: (body: PortalRefillRequest) => post<{ id: string; status: string }>('/portal/refill-requests', body, { idempotent: true }),
  threads: (p: { cursor?: string; limit?: number } = {}) => get<Page<PortalThreadSummary>>('/portal/message-threads', { cursor: p.cursor, limit: p.limit }),
  /**
   * Urgent-symptom screen runs server-side; response may be deterministic guidance instead of "sent". A positive
   * screen always carries a non-empty subject/body (R-011). Appending to an escalated thread needs that thread's ETag.
   */
  createThread: async (body: CreatePortalThreadRequest, escalatedThreadEtag?: string | null) => {
    const r = await postVersioned<CreatePortalThreadResponse>('/portal/message-threads', withUrgentDefaults(body), {
      idempotent: true,
      ifMatch: body.escalatedThreadId ? escalatedThreadEtag ?? null : null,
    });
    return { ...r.data, etag: r.etag } as CreatePortalThreadResponse & { etag: string | null };
  },
  /** If-Match: the thread's ETag / W/"rowVersion". */
  reply: (t: string | PortalThreadSummary, body: string, etag?: string | null) =>
    post<PortalReplyResponse>(`/portal/message-threads/${seg(idOf(t))}:reply`, { body }, { idempotent: true, ifMatch: tagOf(t, etag) }),
  documents: (p: { cursor?: string; limit?: number } = {}) => get<Page<PortalDocument>>('/portal/documents', { cursor: p.cursor, limit: p.limit }),
  /** Released, scanned-clean documents only. Text (utf8) or binary (base64): use `documentBlob()` to download. */
  documentContent: (id: string) => get<PortalDocumentContent>(`/portal/documents/${seg(id)}/content`),
  forms: (p: { cursor?: string; limit?: number } = {}) => get<Page<PortalForm>>('/portal/forms', { cursor: p.cursor, limit: p.limit }),
  submitForm: (formId: string, answers: FormResponse['answers']) => post<FormResponse>('/portal/form-responses', { formId, answers }, { idempotent: true }),
  requests: (p: { cursor?: string; limit?: number } = {}) => get<Page<PortalRequest>>('/portal/requests', { cursor: p.cursor, limit: p.limit }),
  createRequest: (body: CreatePortalRequest) => post<PortalRequest>('/portal/requests', body, { idempotent: true }),
  proxies: (p: { cursor?: string; limit?: number } = {}) => get<Page<PortalProxyGrant>>('/portal/proxies', { cursor: p.cursor, limit: p.limit }),
  revokeProxy: (g: string | PortalProxyGrant, etag?: string | null) =>
    post<PortalProxyGrant>(`/portal/proxies/${seg(idOf(g))}:revoke`, {}, { idempotent: true, ifMatch: tagOf(g, etag) }),
};
}
export const portalApi = { ...portalForPatient(), forPatient: portalForPatient };
